import { ref as _ref } from 'vue'
import { getModule } from 'vuex-module-decorators';
import { AiChatService, PostAiChatHistoryDto } from '@/api';
import store from '@/store';
import authModule from '@/store/modules/auth';
import broadcast from '@/utils/broadcast';
import getEnv from '@/utils/env';
import { trackAgentEvent } from '@/utils/pendo-agent';
import {
  aiChatEnabled,
  refreshAiChatAvailability,
} from './ai-chat-availability';
import { tools, executors } from './tools/registry';
import injectCustomCloseButton from './button-injection';
import { ToolDefinition } from './tools/types';

let widgetMounted = _ref(false);
let isInitialized = _ref(false);
let isChatOpen = _ref(false);

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ChainlitFunctionCallDetail {
  name: string;
  args: Record<string, unknown>;
  callback: (result: Record<string, unknown> | { error: string } | { success: boolean } | { tools: ToolDefinition[] }) => void;
}

interface ChainlitFunctionCallEvent extends CustomEvent {
  detail: ChainlitFunctionCallDetail;
}

interface ChainlitWidgetConfig {
  chainlitServer: string;
  accessToken: string;
  theme: 'dark' | 'light';
  button: {
    containerId: string;
  };
  customCssUrl: string;
}

interface ChainlitWindow extends Window {
  mountChainlitWidget?: (config: ChainlitWidgetConfig) => void;
  unmountChainlitWidget?: () => void;
  toggleChainlitCopilot?: () => void;
}

type ToolExecutor = (args: Record<string, unknown>) => Promise<unknown>;

// ============================================================================
// PENDO AGENT ANALYTICS
// ============================================================================

const getPendoAgentId = (): string | undefined => {
  if (getEnv('VUE_APP_PENDO_ENABLED') !== 'true') {
    return undefined;
  }
  const agentId = getEnv('VUE_APP_PENDO_AI_AGENT_ID');
  return agentId || undefined;
};

const asString = (value: unknown): string | undefined => (typeof value === 'string' && value.length > 0 ? value : undefined);

const newMessageId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const trackConversationTurn = (args: Record<string, unknown>): void => {
  const agentId = getPendoAgentId();
  const conversationId = asString(args.session_id);
  const userInput = asString(args.user_input);
  const aiOutput = asString(args.ai_output);

  if (!agentId || !conversationId || userInput === undefined || aiOutput === undefined) {
    return;
  }

  const promptMessageId = asString(args.user_message_id) || newMessageId();
  let responseMessageId = asString(args.ai_message_id) || newMessageId();
  if (responseMessageId === promptMessageId) {
    responseMessageId = newMessageId();
  }

  trackAgentEvent('prompt', {
    agentId,
    conversationId,
    messageId: promptMessageId,
    content: userInput,
  });

  trackAgentEvent('agent_response', {
    agentId,
    conversationId,
    messageId: responseMessageId,
    content: aiOutput,
  });
};

// ============================================================================
// TOOL MANAGEMENT
// ============================================================================

/**
 * Get tool definitions for AI Chat
 */
const getToolDefinitions = (): ToolDefinition[] => {
  try {
    return Object.values(tools);
  } catch {
    return [];
  }
};

/**
 * Execute a tool by name with the given arguments
 */
const executeTool = async (
  name: string,
  args: Record<string, unknown>,
): Promise<unknown> => {
  const executor = executors[name as keyof typeof executors] as ToolExecutor | undefined;

  if (!executor) {
    throw new Error(`Unknown tool: ${name}`);
  }

  return executor(args);
};

// ============================================================================
// EVENT HANDLERS
// ============================================================================

const handleFunctionCall = async (e: Event): Promise<void> => {
  const customEvent = e as ChainlitFunctionCallEvent;
  const { name, args, callback } = customEvent.detail;

  // Handle special case for getting tool definitions
  if (name === 'get_tools') {
    try {
      const toolDefinitions = getToolDefinitions();
      callback({ tools: toolDefinitions });
    } catch (error) {
      callback({ error: (error as Error).message, tools: [] });
    }
    // Handle special case for saving chat history
  } else if (name === 'save_chat_history') {
    // This bridge only runs for completed user/assistant turns; the welcome message never reaches it.
    trackConversationTurn(args);
    await AiChatService.postAiChatHistory({
      requestBody: {
        sessionId: args.session_id,
        userInput: args.user_input,
        aiOutput: args.ai_output,
      } as PostAiChatHistoryDto,
    });
    callback({ success: true });
  } else {
    try {
      const result = await executeTool(name, args);
      callback(result as Record<string, unknown>);
    } catch (error) {
      // Return error details for LLM iterations
      callback({ error: (error as Error).message });
    }
  }
};

// ============================================================================
// WIDGET MANAGEMENT
// ============================================================================

const CHAINLIT_SCRIPT_SELECTOR = 'script[src*="/copilot/index.js"]';

// Remove the Chainlit copilot script so the next mount loads fresh and opens a new backend connection.
const removeChainlitScript = (): void => {
  document.querySelectorAll(CHAINLIT_SCRIPT_SELECTOR).forEach((el) => el.remove());
};

const loadChainlitScript = async (chainlitUrl: string): Promise<void> => {
  // Check if env var is available and if script is already loaded
  if (!chainlitUrl || document.querySelector(CHAINLIT_SCRIPT_SELECTOR)) {
    return;
  }

  // Create and load the script dynamically
  const script = document.createElement('script');
  script.src = `${chainlitUrl}/copilot/index.js`;
  script.async = true;

  await new Promise<void>((resolve, reject) => {
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Chainlit script'));
    document.head.appendChild(script);
  });
};

const mountChainlitWidget = async (): Promise<void> => {
  if (widgetMounted.value) return;

  // Plan/UI availability
  if (!aiChatEnabled.value) return;

  const container = document.getElementById('chainlit-container');
  if (!container) return;

  const chainlitUrl = getEnv('VUE_APP_CHAINLIT_URL');
  if (!chainlitUrl) return;

  try {
    // Load the script first
    await loadChainlitScript(chainlitUrl);

    // Poll until the widget function is registered (script may initialize asynchronously after onload)
    await new Promise<void>((resolve, reject) => {
      const deadline = Date.now() + 3000;
      const interval = setInterval(() => {
        if ((window as ChainlitWindow).mountChainlitWidget) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() > deadline) {
          clearInterval(interval);
          reject(new Error('Chainlit widget did not initialize in time'));
        }
      }, 20);
    });

    const chainlitWindow = window as ChainlitWindow;
    if (!chainlitWindow.mountChainlitWidget) return;

    const chainlitToken = await AiChatService.getAiChatAuthToken();

    chainlitWindow.mountChainlitWidget({
      chainlitServer: chainlitUrl,
      accessToken: chainlitToken.token,
      theme: 'dark',
      button: {
        containerId: 'chainlit-container',
      },
      customCssUrl: '/ai-chat/chainlit-widget.css',
    });

    widgetMounted.value = true;
  } catch (error) {
    widgetMounted.value = false;
  }
};

// ============================================================================
// WIDGET STATE MANAGEMENT
// ============================================================================

const closeWidget = (): void => {
  if (!widgetMounted.value) return;

  const chainlitWindow = window as ChainlitWindow;
  if (typeof chainlitWindow.toggleChainlitCopilot === 'function' && isChatOpen.value) {
    chainlitWindow.toggleChainlitCopilot();
  }
  isChatOpen.value = false;
};

const unmountWidget = (): void => {
  if (!widgetMounted.value) {
    return;
  }

  isChatOpen.value = false;

  const chainlitWindow = window as ChainlitWindow;
  if (chainlitWindow.unmountChainlitWidget) {
    chainlitWindow.unmountChainlitWidget();
  }

  // Remove the copilot script so the next mount loads fresh and opens a new backend connection
  // (otherwise neither on_chat_start nor on_chat_resume are called after remount)
  removeChainlitScript();

  widgetMounted.value = false;
};

const checkAndMountWidget = async (): Promise<void> => {
  const auth = getModule(authModule, store);
  if (!auth.user) {
    unmountWidget();
    return;
  }

  await refreshAiChatAvailability();

  if (!aiChatEnabled.value) {
    unmountWidget();
    return;
  }

  await mountChainlitWidget();
};

const broadcastPreferenceChange = (): void => {
  broadcast.postMessage({ type: 'chainlit-preference-changed' });
};

const handleBroadcastMessage = (event: MessageEvent): void => {
  if (event.data?.type !== 'chainlit-preference-changed') return;
  checkAndMountWidget();
};

const registerEventListeners = (): void => {
  if (isInitialized.value) {
    return;
  }

  window.addEventListener('chainlit-call-fn', handleFunctionCall);
  broadcast.addEventListener('message', handleBroadcastMessage);
  isInitialized.value = true;
};

const openWidget = async (): Promise<void> => {
  registerEventListeners();

  // Check if widget is mounted, if not try to mount it first
  if (!widgetMounted.value) {
    await checkAndMountWidget();
  }

  // Call native Chainlit method to toggle the widget
  const chainlitWindow = window as ChainlitWindow;
  if (typeof chainlitWindow.toggleChainlitCopilot !== 'function') {
    return;
  }

  chainlitWindow.toggleChainlitCopilot();
  isChatOpen.value = !isChatOpen.value;

  if (isChatOpen.value) {
    injectCustomCloseButton(closeWidget);
  }
};

// ============================================================================
// PUBLIC API
// ============================================================================

const initialize = async (): Promise<void> => {
  // Prevent multiple initializations
  if (isInitialized.value) {
    return;
  }

  registerEventListeners();
  await checkAndMountWidget();
};

const refreshWidgetVisibility = async (): Promise<void> => {
  registerEventListeners();
  await checkAndMountWidget();
  broadcastPreferenceChange();
};

const handleUserLogout = (): void => {
  unmountWidget();
  isInitialized.value = false;
};

const useAiChat = () => ({
  initialize,
  refreshWidgetVisibility,
  handleUserLogout,
  openWidget,
  aiChatEnabled,
  isChatOpen: () => isChatOpen.value,
});

export default useAiChat;
